import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Lock, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import { Button } from '@foundation/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@foundation/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@foundation/components/ui/select';
import { Textarea } from '@foundation/components/ui/textarea';
import { toast } from '@foundation/hooks/use-toast';
import { getActiveWorkspaceId } from '../../../header/activeWorkspace';
import { getOrgContext, saveOrgContext } from '../services/organizationContextService';
import { useOrgAdmin } from '../hooks/useOrgAdmin';
import RevisrLayout from '../components/RevisrLayout';

const INDUSTRY_OPTIONS = [
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'construction', label: 'Construction' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'oil_gas', label: 'Oil & Gas' },
  { value: 'mining', label: 'Mining' },
  { value: 'transport_logistics', label: 'Transportation & Logistics' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'government', label: 'Government' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'other', label: 'Other' },
];

const SIZE_OPTIONS = [
  { value: '1-10', label: '1–10 employees' },
  { value: '11-50', label: '11–50 employees' },
  { value: '51-200', label: '51–200 employees' },
  { value: '201-500', label: '201–500 employees' },
  { value: '500+', label: '500+ employees' },
];

const profileSchema = z.object({
  industry: z.string().optional(),
  org_size: z.string().optional(),
  maturityLevel: z.string().optional(),
  organizationContext: z.string().optional(),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

type OrgProfileFormProps = {
  /** Render the page-level h1 header. Set false when embedding inside another section. */
  showHeader?: boolean;
};

export const OrgProfileForm = ({ showHeader = true }: OrgProfileFormProps) => {
  const orgId = getActiveWorkspaceId() ?? '';
  const { isOrgAdmin, isLoading: roleLoading } = useOrgAdmin();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { industry: '', org_size: '', maturityLevel: '', organizationContext: '' },
  });

  useEffect(() => {
    if (!orgId) { setIsLoading(false); return; }

    getOrgContext(orgId)
      .then(ctx => {
        form.reset({
          industry: ctx?.profile?.industry ?? '',
          org_size: ctx?.profile?.org_size ?? '',
          maturityLevel: ctx?.profile?.maturityLevel ?? '',
          organizationContext: ctx?.organizationContext ?? '',
        });
      })
      .catch(err => console.error('[OrgProfile] load error:', err))
      .finally(() => setIsLoading(false));
  }, [orgId, form]);

  const onSubmit = async (values: ProfileFormValues) => {
    if (!orgId) return;
    setIsSaving(true);
    try {
      const existing = await getOrgContext(orgId).catch(() => null);
      await saveOrgContext(
        {
          ...existing,
          organizationContext: values.organizationContext || undefined,
          profile: {
            ...existing?.profile,
            industry: values.industry || undefined,
            org_size: values.org_size || undefined,
            maturityLevel: values.maturityLevel || undefined,
          },
        },
        orgId
      );
      toast({ title: 'Profile saved', description: 'Organisation profile updated successfully.' });
    } catch (err) {
      console.error('[OrgProfile] save error:', err);
      toast({ title: 'Save failed', description: 'Something went wrong. Please try again.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const disabled = !isOrgAdmin || roleLoading;

  if (isLoading || roleLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#33b864]" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          {showHeader && (
            <h1 className="text-xl font-bold text-foreground leading-tight">Organisation Profile</h1>
          )}
          <p className="text-sm text-muted-foreground mt-0.5">
            {isOrgAdmin
              ? 'Used when generating policies to tailor output to your organisation.'
              : 'Contact your admin to make changes.'}
          </p>
        </div>
        {!isOrgAdmin && (
          <span className="inline-flex items-center gap-1.5 shrink-0 px-2.5 py-1 rounded-full bg-muted text-xs text-muted-foreground">
            <Lock className="h-3 w-3" /> Read only
          </span>
        )}
      </div>

      <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

            {/* Industry + Size */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="industry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Industry</FormLabel>
                    <Select disabled={disabled} onValueChange={field.onChange} value={field.value ?? ''}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {INDUSTRY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="org_size"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organisation Size</FormLabel>
                    <Select disabled={disabled} onValueChange={field.onChange} value={field.value ?? ''}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SIZE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Governance Maturity */}
            <FormField
              control={form.control}
              name="maturityLevel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Governance Maturity</FormLabel>
                  <Select disabled={disabled} onValueChange={field.onChange} value={field.value ?? ''}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select maturity level…" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Policy Tailoring Notes */}
            <div className="pt-2 border-t border-border">
              <FormField
                control={form.control}
                name="organizationContext"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-semibold text-foreground">
                      Policy Tailoring Notes
                    </FormLabel>
                    <FormDescription>
                      Provide extra details to further tailor generated policies{' '}
                      <span className="text-muted-foreground">(Optional)</span>
                    </FormDescription>
                    <p className="text-sm text-muted-foreground -mt-1 mb-1">
                      e.g. specific regulatory requirements, internal processes, risk appetite, governance structure, or anything the AI should emphasise or avoid.
                    </p>
                    <FormControl>
                      <Textarea
                        placeholder="Add any organisation-specific context here…"
                        className="min-h-[150px] resize-y"
                        disabled={disabled}
                        {...field}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      This is combined with your Org Profile above when generating policies.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {isOrgAdmin && (
              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="h-9 px-5 gap-2 bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  {isSaving
                    ? <><Loader2 className="h-4 w-4 animate-spin" />Saving…</>
                    : <><Save className="h-4 w-4" />Save Profile</>}
                </Button>
              </div>
            )}
          </form>
        </Form>
    </div>
  );
};

export const OrganisationProfilePage = () => (
  <RevisrLayout>
    <div className="px-4 py-8 md:px-8 max-w-2xl mx-auto">
      <OrgProfileForm />
    </div>
  </RevisrLayout>
);
